import moment from "moment-timezone";
import {
    calculateSectionsWithAutomaticBreaks,
    getAutomaticBreaks,
    getSectionsBounds
} from "./sections";
import { combineDateAndTime, renderDurationAsFormat } from "../../../../../utils/helpers/date";

export const getLevelNames = (allocatedGroups: any) =>
    (allocatedGroups || []).map((group: any) => group.level.replaceAll("_", ""));

const isBreak = (section: any) => section?.type === "break" && section?.start && section?.end;

const isSameMinute = (a: any, b: any) => moment(a).isSame(moment(b), "minute");

const byStart = (a: any, b: any) => moment(a.start).diff(moment(b.start));

/**
 * Advanced -> Simple: flattens the sections back onto the top level fields,
 * turning the break sections into the manual breaks list.
 * Returns the values to patch onto the form, or null when there is nothing to flatten.
 */
export const sectionsToSimpleValues = (values: any, levelNames: string[]) => {
    const sections = values.sections;
    const firstSection = sections?.[0];
    if (!firstSection) {
        return null;
    }

    const workSection = sections.find((section: any) => section?.type !== "break") || firstSection;
    const { start, end } = getSectionsBounds(sections);

    return {
        payCode: workSection.payCode,
        job: workSection.job,
        location: workSection.location,
        skills: workSection.skills,
        licenses: workSection.licenses,
        certifications: workSection.certifications,
        ...Object.fromEntries(levelNames.map((level) => [level, workSection[level]])),
        breaks: sections
            .filter(isBreak)
            .map((section: any) => ({
                ...section,
                start: moment(section.start).toDate(),
                end: moment(section.end).toDate()
            })),
        ...(start ? { startDate: start } : {}),
        ...(end ? { endDate: end } : {})
    };
};

/**
 * Simple -> Advanced: expands the shift into sections, splitting it around the
 * manual breaks when there are any, otherwise creating a single regular section
 * (with automatic breaks applied for a new event).
 * Returns the sections to set, or null when the current sections already represent them.
 */
export const simpleValuesToSections = (
    values: any,
    { levelNames, isCreating, calcGroupBreaks }: { levelNames: string[], isCreating?: boolean, calcGroupBreaks?: any }
) => {
    const { startDate: start, endDate: end, payCode } = values;
    const commonProps = {
        job: values.job,
        location: values.location,
        skills: values.skills,
        licenses: values.licenses,
        certifications: values.certifications,
        ...Object.fromEntries(levelNames.map((level) => [level, values[level]]))
    };

    const createSection = (type: string, sectionStart: any, sectionEnd: any, sectionPayCode: any) => ({
        type,
        start: sectionStart,
        end: sectionEnd,
        payCode: sectionPayCode,
        duration: renderDurationAsFormat(moment(sectionEnd).diff(moment(sectionStart), "seconds"), "HH:mm"),
        ...commonProps
    });

    const manualBreaks = values.breaks;
    if (manualBreaks?.length && start && end) {
        const shiftStart = moment(start);
        let shiftEnd = moment(end);
        if (shiftEnd.isSameOrBefore(shiftStart)) {
            shiftEnd = moment(shiftEnd).add(1, "day");
        }

        const sortedBreaks = manualBreaks
            .filter((br: any) => br?.start && br?.end)
            .map((br: any) => {
                const breakStart = combineDateAndTime(shiftStart, moment(br.start));
                let breakEnd = combineDateAndTime(shiftStart, moment(br.end));
                if (moment(breakEnd).isBefore(breakStart)) {
                    breakEnd = moment(breakEnd).add(1, "day");
                }
                return { ...br, start: breakStart, end: breakEnd };
            })
            .filter((br: any) => moment(br.start).isBefore(shiftEnd) && moment(br.end).isAfter(shiftStart))
            .sort(byStart);

        const existingSections = values.sections ?? [];
        const existingBounds = getSectionsBounds(existingSections);
        const existingBreaks = existingSections.filter(isBreak).slice().sort(byStart);

        const sectionsAlreadyMatchBreaks = existingSections.length > 0
            && existingBreaks.length === sortedBreaks.length
            && isSameMinute(existingBounds.start, shiftStart)
            && isSameMinute(existingBounds.end, shiftEnd)
            && existingBreaks.every((br: any, index: number) => (
                isSameMinute(br.start, sortedBreaks[index].start)
                && isSameMinute(br.end, sortedBreaks[index].end)
            ));

        if (sectionsAlreadyMatchBreaks) {
            return null;
        }

        const breakSections: any[] = [];
        let cursor = shiftStart;
        sortedBreaks.forEach((br: any) => {
            if (moment(br.start).isAfter(cursor)) {
                breakSections.push(createSection("regular", cursor, br.start, payCode));
            }
            breakSections.push(createSection("break", br.start, br.end, br.payCode ?? payCode));
            cursor = br.end;
        });

        if (breakSections.length) {
            if (moment(shiftEnd).isAfter(cursor)) {
                breakSections.push(createSection("regular", cursor, shiftEnd, payCode));
            }
            return breakSections;
        }
    }

    if (values.sections?.length) {
        return null;
    }

    const baseSections = [{
        ...createSection("regular", start, end, payCode),
        canCalculateAutomaticBreaks: isCreating
    }];

    const autoBreaks = getAutomaticBreaks(calcGroupBreaks, baseSections, baseSections[0]) || [];
    return autoBreaks.length > 0
        ? calculateSectionsWithAutomaticBreaks(baseSections, autoBreaks)
        : baseSections;
};
